import { useCallback, useEffect, useMemo, useState } from 'react';
import { Report } from 'organisms/CloudOpsReportAdmin/types';
import {
  isReportBulkApprovable,
  isReportBulkSelectable,
  isReportBulkUpdatable,
} from 'organisms/CloudOpsReportAdmin/bulk/reportEligibility';

type SelectableReport = Pick<
  Report,
  | 'id'
  | 'invoice_number'
  | 'is_invoice_created'
  | 'month'
  | 'year'
  | 'error_state'
  | 'project'
  | 'invoice_creation_approval'
  | 'pm_approval'
  | 'contract_type'
  | 'total_amount'
  | 'adjustment_amount'
  | 'monthly_fee'
  | 'monthly_hourly_total_amount'
>;

interface Options {
  currentPageReports: SelectableReport[];
  resetKey: string;
}

export function useReportBulkSelection({
  currentPageReports,
  resetKey,
}: Options) {
  const [selectedReports, setSelectedReports] = useState<SelectableReport[]>(
    []
  );
  const selectedIds = useMemo(
    () => selectedReports.map(({ id }) => id),
    [selectedReports]
  );
  const hasApprovableSelection = useMemo(
    () => selectedReports.some((report) => isReportBulkApprovable(report)),
    [selectedReports]
  );
  const hasUpdatableSelection = useMemo(
    () => selectedReports.some((report) => isReportBulkUpdatable(report)),
    [selectedReports]
  );
  const selectableCurrentPageReports = useMemo(
    () => currentPageReports.filter((report) => isReportBulkSelectable(report)),
    [currentPageReports]
  );
  const eligibleCurrentPageIds = useMemo(
    () => selectableCurrentPageReports.map(({ id }) => id),
    [selectableCurrentPageReports]
  );
  const selectedCurrentPageCount = eligibleCurrentPageIds.filter((id) =>
    selectedIds.includes(id)
  ).length;
  const allCurrentPageSelected =
    eligibleCurrentPageIds.length > 0 &&
    selectedCurrentPageCount === eligibleCurrentPageIds.length;
  const someCurrentPageSelected =
    selectedCurrentPageCount > 0 && !allCurrentPageSelected;

  const clearSelection = useCallback(() => {
    setSelectedReports([]);
  }, []);

  useEffect(() => {
    clearSelection();
  }, [clearSelection, resetKey]);

  const toggleReport = useCallback((report: SelectableReport) => {
    if (!isReportBulkSelectable(report)) return;
    setSelectedReports((current) =>
      current.some(({ id }) => id === report.id)
        ? current.filter(({ id }) => id !== report.id)
        : [...current, report]
    );
  }, []);

  const toggleAllCurrentPage = useCallback(() => {
    setSelectedReports(
      allCurrentPageSelected ? [] : selectableCurrentPageReports
    );
  }, [allCurrentPageSelected, selectableCurrentPageReports]);

  return useMemo(
    () => ({
      selectedIds,
      selectedCount: selectedIds.length,
      hasApprovableSelection,
      hasUpdatableSelection,
      allCurrentPageSelected,
      someCurrentPageSelected,
      isSelected: (id: number) => selectedIds.includes(id),
      toggleReport,
      toggleAllCurrentPage,
      clearSelection,
    }),
    [
      allCurrentPageSelected,
      clearSelection,
      hasApprovableSelection,
      hasUpdatableSelection,
      selectedIds,
      someCurrentPageSelected,
      toggleAllCurrentPage,
      toggleReport,
    ]
  );
}
